import { useState, useCallback } from "react";
import {
	Drawer,
	DrawerBody,
	DrawerContent,
	DrawerHeader,
	DrawerOverlay,
	Flex,
} from "@chakra-ui/react";
import { FiX, FiAlertCircle } from "react-icons/fi";
import { cn } from "utils/helpers";
import { useDeclineInvitation } from "../../../../../hooks/usePanelScoringQueries";

interface DeclineDrawerProps {
	evalId: number;
	isOpen: boolean;
	memberId: number;
	/**
	 * TASK-028: nullable — an external panel member's `rfxData` may not have
	 * resolved; `useDeclineInvitation` guards rather than building a bad URL.
	 */
	rfxId: number | null;
	rfxLeadEmail?: string;
	rfxLeadName?: string;
	rfxTitle?: string;
	onClose: () => void;
	onDeclined?: () => void;
}

export const DeclineDrawer: React.FC<DeclineDrawerProps> = ({
	isOpen,
	onClose,
	rfxId,
	evalId,
	memberId,
	rfxLeadName,
	rfxLeadEmail,
	rfxTitle,
	onDeclined,
}) => {
	const [reason, setReason] = useState("");
	const declineMutation = useDeclineInvitation(rfxId, evalId);

	const handleSubmit = useCallback(() => {
		declineMutation.mutate(
			{ memberId, payload: { reason: reason.trim() } },
			{
				onSuccess: () => {
					setReason("");
					onDeclined?.();
					onClose();
				},
			}
		);
	}, [declineMutation, memberId, reason, onDeclined, onClose]);

	const isReasonValid = reason.trim().length > 0;

	return (
		<Drawer
			placement="right"
			isOpen={isOpen}
			onClose={onClose}
			size="lg"
		>
			<DrawerOverlay onClick={onClose} />
			<DrawerContent maxWidth="640px">
				<DrawerHeader className="border-b-2">
					<Flex justifyContent="space-between" align="center">
						<div>
							<p className="text-xl font-semibold text-gray-900">
								Decline Evaluation Panel Invitation
							</p>
							<p className="text-sm font-normal text-gray-500">
								Let the RFx Lead know why you are unable to participate.
							</p>
						</div>
						<button
							onClick={onClose}
							className="rounded-lg p-2 hover:bg-gray-100"
							aria-label="Close drawer"
							data-testid="close-decline-drawer"
						>
							<FiX size={20} />
						</button>
					</Flex>
				</DrawerHeader>
				<DrawerBody>
					<div className="py-4 space-y-6">
						{/* Email preview header */}
						<div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
							<div className="space-y-2 text-sm">
								<div className="flex gap-2">
									<span className="font-medium text-gray-500 w-12">To:</span>
									<span className="text-gray-900">
										{rfxLeadName || "RFx Lead"}
										{rfxLeadEmail && (
											<span className="text-gray-500"> &lt;{rfxLeadEmail}&gt;</span>
										)}
									</span>
								</div>
								<div className="flex gap-2">
									<span className="font-medium text-gray-500 w-12">Re:</span>
									<span className="text-gray-900">
										Declining Evaluation Invitation
										{rfxTitle && ` — ${rfxTitle}`}
									</span>
								</div>
							</div>
						</div>

						{/* Reason field */}
						<div>
							<label
								htmlFor="decline-reason"
								className="mb-1.5 block text-sm font-medium text-gray-700"
							>
								Reason for declining
								<span className="text-red-500"> *</span>
							</label>
							<textarea
								id="decline-reason"
								value={reason}
								onChange={(e) => setReason(e.target.value)}
								placeholder="Please explain why you are unable to participate in this evaluation..."
								rows={5}
								maxLength={2000}
								className={cn(
									"w-full rounded-lg border px-3 py-2 text-sm text-gray-900 placeholder-gray-400 transition-colors",
									"focus:border-[#0031DD] focus:outline-none focus:ring-1 focus:ring-[#0031DD]",
									reason.trim().length === 0 && reason.length > 0
										? "border-red-300"
										: "border-gray-300"
								)}
								data-testid="decline-reason-input"
							/>
							<div className="mt-1 flex items-center justify-between">
								{!isReasonValid && reason.length > 0 && (
									<p className="text-xs text-red-500">
										A reason is required to decline.
									</p>
								)}
								<span className="ml-auto text-xs text-gray-400">
									{reason.length}/2000
								</span>
							</div>
						</div>

						{declineMutation.isError && (
							<div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2">
								<FiAlertCircle size={14} className="shrink-0 text-red-500" />
								<p className="text-xs text-red-700">
									Failed to decline invitation. Please try again.
								</p>
							</div>
						)}

						{/* Actions */}
						<div className="flex items-center justify-end gap-3 border-t border-gray-200 pt-4">
							<button
								type="button"
								onClick={onClose}
								disabled={declineMutation.isPending}
								className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
								data-testid="cancel-decline-btn"
							>
								Cancel
							</button>
							<button
								type="button"
								onClick={handleSubmit}
								disabled={!isReasonValid || declineMutation.isPending}
								className={cn(
									"rounded-lg px-4 py-2 text-sm font-medium transition-colors",
									"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2",
									isReasonValid
										? "bg-red-600 text-white hover:bg-red-700 active:bg-red-800"
										: "bg-gray-200 text-gray-400 cursor-not-allowed"
								)}
								data-testid="confirm-decline-btn"
							>
								{declineMutation.isPending ? "Sending..." : "Decline Invitation"}
							</button>
						</div>
					</div>
				</DrawerBody>
			</DrawerContent>
		</Drawer>
	);
};
